import React, { useEffect, useMemo, useState } from "react";
import {
  fetchResourceItems,
  createResourceItem,
  fetchResourceTypes,
  fetchCategories,
  fetchUom,
  fetchDecors,
  fetchItemAttributes,
  fetchResourceItemFull,
  updateResourceItemFull,
  uploadResourceItemImage,
  deleteResourceItemImage,
  updateResourceItemImage,
  exportResourceItemsCsv,
  importResourceItemsCsv,
} from "../services/erpMasterData";
import { getFirebaseToken } from "../utils/getFirebaseToken";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://modularobjects.com";

function emptyForm() {
  return {
    sku: "",
    name: "",
    short_name: "",
    description: "",
    brand: "",
    manufacturer: "",
    model: "",
    color: "",
    finish: "",
    thickness: "",
    width: "",
    length: "",
    weight: "",
    resource_type_id: "",
    category_id: "",
    decor_id: "",
    uom_id: "",
    tracking_mode: "QUANTITY",
    item_kind: "MATERIAL",
    default_purchase_price: "",
    default_issue_price: "",
    currency: "RSD",
    min_stock_qty: "",
    reorder_qty: "",
    is_active: 1,
    spec_json: "",
    attributes: [],
    images: [],
  };
}

function numberOrNull(v) {
  if (v === "" || v === null || typeof v === "undefined") return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function normalizeImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url}`;
}

export default function ResourceItemsPage() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);

  const [resourceTypes, setResourceTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [decors, setDecors] = useState([]);
  const [attributeCatalog, setAttributeCatalog] = useState([]);

  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const selectedItemPreview = useMemo(() => {
    return rows.find((x) => Number(x.id) === Number(editingId)) || null;
  }, [rows, editingId]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getFirebaseToken();

      const [
        itemsRes,
        typesRes,
        categoriesRes,
        uomRes,
        decorsRes,
        attributesRes,
      ] = await Promise.all([
        fetchResourceItems(token),
        fetchResourceTypes(token),
        fetchCategories(token),
        fetchUom(token),
        fetchDecors(token),
        fetchItemAttributes(token),
      ]);

      setRows(itemsRes.rows || []);
      setResourceTypes(typesRes.rows || []);
      setCategories(categoriesRes.rows || []);
      setUoms(uomRes.rows || []);
      setDecors(decorsRes.rows || []);
      setAttributeCatalog(attributesRes.rows || []);
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          e.message ||
          "Failed to load resource items."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    let next = [...rows];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      next = next.filter((r) =>
        [
          r.sku,
          r.name,
          r.short_name,
          r.brand,
          r.manufacturer,
          r.model,
          r.decor_name,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    }

    if (activeOnly) {
      next = next.filter((r) => Number(r.is_active) === 1);
    }

    setFilteredRows(next);
  }, [rows, search, activeOnly]);

  const setField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditingId(null);
    setError("");
  };

  const startEdit = async (row) => {
    try {
      const token = await getFirebaseToken();
      const res = await fetchResourceItemFull(token, row.id);
      const data = res.data || res;

      setEditingId(row.id);
      setForm({
        ...emptyForm(),
        ...(data.item || {}),
        resource_type_id: data.item?.resource_type_id || "",
        category_id: data.item?.category_id || "",
        decor_id: data.item?.decor_id || "",
        uom_id: data.item?.uom_id || "",
        spec_json:
          typeof data.item?.spec_json === "string"
            ? data.item.spec_json
            : data.item?.spec_json
            ? JSON.stringify(data.item.spec_json, null, 2)
            : "",
        attributes: (data.attributes || []).map((a) => ({
          id: a.id,
          attribute_id: a.attribute_id || "",
          attribute_code: a.attribute_code || "",
          attribute_name: a.attribute_name || "",
          value_text: a.value_text || "",
          value_number: a.value_number ?? "",
          value_boolean:
            a.value_boolean === null || typeof a.value_boolean === "undefined"
              ? ""
              : Number(a.value_boolean),
          value_json: a.value_json
            ? typeof a.value_json === "string"
              ? a.value_json
              : JSON.stringify(a.value_json, null, 2)
            : "",
          value_type: a.value_type || "text",
        })),
        images: data.images || [],
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to load full item."
      );
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");

    try {
      const token = await getFirebaseToken();

      if (!form.sku || !form.name) {
        throw new Error("SKU and Name are required.");
      }

      let parsedSpecJson = null;
      if (form.spec_json && String(form.spec_json).trim()) {
        try {
          parsedSpecJson = JSON.parse(form.spec_json);
        } catch {
          throw new Error("spec_json must be valid JSON.");
        }
      }

      const normalizedAttributes = (form.attributes || []).map((attr) => {
        let valueJson = null;
        if (attr.value_json && String(attr.value_json).trim()) {
          try {
            valueJson = JSON.parse(attr.value_json);
          } catch {
            throw new Error(
              `Attribute "${attr.attribute_name || attr.attribute_code || attr.attribute_id}" has invalid value_json.`
            );
          }
        }

        return {
          attribute_id: numberOrNull(attr.attribute_id),
          value_text: attr.value_text || null,
          value_number: numberOrNull(attr.value_number),
          value_boolean:
            attr.value_boolean === "" ? null : numberOrNull(attr.value_boolean),
          value_json: valueJson,
        };
      });

      const payload = {
        sku: form.sku || null,
        name: form.name || null,
        short_name: form.short_name || null,
        description: form.description || null,
        brand: form.brand || null,
        manufacturer: form.manufacturer || null,
        model: form.model || null,
        color: form.color || null,
        finish: form.finish || null,
        thickness: numberOrNull(form.thickness),
        width: numberOrNull(form.width),
        length: numberOrNull(form.length),
        weight: numberOrNull(form.weight),
        resource_type_id: numberOrNull(form.resource_type_id),
        category_id: numberOrNull(form.category_id),
        decor_id: numberOrNull(form.decor_id),
        uom_id: numberOrNull(form.uom_id),
        tracking_mode: form.tracking_mode || "QUANTITY",
        item_kind: form.item_kind || "MATERIAL",
        default_purchase_price: numberOrNull(form.default_purchase_price) ?? 0,
        default_issue_price: numberOrNull(form.default_issue_price) ?? 0,
        currency: form.currency || "RSD",
        min_stock_qty: numberOrNull(form.min_stock_qty) ?? 0,
        reorder_qty: numberOrNull(form.reorder_qty) ?? 0,
        is_active: Number(form.is_active) ? 1 : 0,
        spec_json: parsedSpecJson,
        attributes: normalizedAttributes.filter((x) => x.attribute_id),
      };

      if (editingId) {
        await updateResourceItemFull(token, editingId, payload);
      } else {
        const created = await createResourceItem(token, payload);
        const newId = created?.id || created?.data?.id || null;
        await load();
        if (newId) {
          await startEdit({ id: newId });
          return;
        }
      }

      await load();
      if (editingId) {
        await startEdit({ id: editingId });
      } else {
        resetForm();
      }
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message ||
          e.message ||
          "Failed to save resource item."
      );
    } finally {
      setSaving(false);
    }
  };

  const addAttributeRow = () => {
    setForm((prev) => ({
      ...prev,
      attributes: [
        ...(prev.attributes || []),
        {
          attribute_id: "",
          attribute_code: "",
          attribute_name: "",
          value_text: "",
          value_number: "",
          value_boolean: "",
          value_json: "",
          value_type: "text",
        },
      ],
    }));
  };

  const setAttributeField = (index, field, value) => {
    setForm((prev) => {
      const next = [...(prev.attributes || [])];
      next[index] = {
        ...next[index],
        [field]: value,
      };
      return { ...prev, attributes: next };
    });
  };

  const removeAttributeRow = (index) => {
    setForm((prev) => ({
      ...prev,
      attributes: (prev.attributes || []).filter((_, i) => i !== index),
    }));
  };

  const onSelectAttribute = (index, attributeId) => {
    const selected = attributeCatalog.find(
      (a) => Number(a.id) === Number(attributeId)
    );

    setForm((prev) => {
      const next = [...(prev.attributes || [])];
      next[index] = {
        ...next[index],
        attribute_id: attributeId ? Number(attributeId) : "",
        attribute_code: selected?.code || "",
        attribute_name: selected?.name || "",
        value_type: selected?.value_type || "text",
      };
      return { ...prev, attributes: next };
    });
  };

  const handleUploadImage = async (file) => {
    if (!editingId) {
      setError("Save the item first, then upload images.");
      return;
    }

    if (!file) return;

    setUploadingImage(true);
    setError("");

    try {
      const token = await getFirebaseToken();
      await uploadResourceItemImage(token, editingId, file, {
        image_type: "main",
        sort_order: (form.images || []).length,
        is_primary: (form.images || []).length === 0 ? 1 : 0,
      });

      const refreshed = await fetchResourceItemFull(token, editingId);
      const data = refreshed.data || refreshed;

      setForm((prev) => ({
        ...prev,
        images: data.images || [],
      }));
      await load();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to upload image."
      );
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const token = await getFirebaseToken();
      await deleteResourceItemImage(token, imageId);

      const refreshed = await fetchResourceItemFull(token, editingId);
      const data = refreshed.data || refreshed;

      setForm((prev) => ({
        ...prev,
        images: data.images || [],
      }));
      await load();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to delete image."
      );
    }
  };

  const handleSetPrimaryImage = async (img) => {
    try {
      const token = await getFirebaseToken();
      await updateResourceItemImage(token, img.id, {
        image_type: img.image_type || "main",
        sort_order: img.sort_order || 0,
        is_primary: 1,
      });

      const refreshed = await fetchResourceItemFull(token, editingId);
      const data = refreshed.data || refreshed;

      setForm((prev) => ({
        ...prev,
        images: data.images || [],
      }));
      await load();
    } catch (e) {
      console.error(e);
      setError(
        e?.response?.data?.message || e.message || "Failed to set primary image."
      );
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Resource Items</h1>
          <p className="text-slate-600">
            Create and maintain internal catalog items, pricing, stock parameters,
            attributes, decors and real uploaded images.
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 rounded-lg border"
          >
            New Item
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-black text-white disabled:opacity-50"
          >
            {saving ? "Saving..." : editingId ? "Update Item" : "Create Item"}
          </button>
        </div>
        <button
  type="button"
  onClick={async () => {
    try {
      const token = await getFirebaseToken();
      const blob = await exportResourceItemsCsv(token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "erp_resource_items.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e.message || "Failed to export CSV.");
    }
  }}
  className="px-4 py-2 rounded-lg border"
>
  Export CSV
</button>

<label className="px-4 py-2 rounded-lg border cursor-pointer">
  Import CSV
  <input
    type="file"
    accept=".csv"
    className="hidden"
    onChange={async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const token = await getFirebaseToken();
        const result = await importResourceItemsCsv(token, file);

        if (result.errors?.length) {
          setError(
            `Import finished with ${result.failed} errors. First error: ${result.errors[0].error}`
          );
        } else {
          setError("");
        }

        await load();
      } catch (err) {
        console.error(err);
        setError(err?.response?.data?.message || err.message || "Failed to import CSV.");
      }
    }}
  />
</label>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
            <h2 className="text-lg font-semibold">
              {editingId ? `Edit Item #${editingId}` : "New Item"}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                placeholder="SKU *"
                value={form.sku || ""}
                onChange={(e) => setField("sku", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Name *"
                value={form.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                className="border rounded-lg px-3 py-2 md:col-span-2"
              />
              <input
                placeholder="Short name"
                value={form.short_name || ""}
                onChange={(e) => setField("short_name", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <select
                value={form.resource_type_id || ""}
                onChange={(e) => setField("resource_type_id", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">Resource type</option>
                {resourceTypes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.name}
                  </option>
                ))}
              </select>

              <select
                value={form.category_id || ""}
                onChange={(e) => setField("category_id", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code ? `${c.code} - ` : ""}
                    {c.name}
                  </option>
                ))}
              </select>

              <select
                value={form.uom_id || ""}
                onChange={(e) => setField("uom_id", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">UOM</option>
                {uoms.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.code} - {u.name}
                  </option>
                ))}
              </select>

              <select
                value={form.item_kind || "MATERIAL"}
                onChange={(e) => setField("item_kind", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="MATERIAL">MATERIAL</option>
                <option value="TOOL">TOOL</option>
                <option value="EQUIPMENT">EQUIPMENT</option>
                <option value="FIXED_ASSET">FIXED_ASSET</option>
              </select>
            </div>

            <textarea
              placeholder="Description"
              value={form.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 min-h-[100px]"
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-4">
              Brand, manufacturer and appearance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <input
                placeholder="Brand"
                value={form.brand || ""}
                onChange={(e) => setField("brand", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Manufacturer"
                value={form.manufacturer || ""}
                onChange={(e) => setField("manufacturer", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Model"
                value={form.model || ""}
                onChange={(e) => setField("model", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <select
                value={form.decor_id || ""}
                onChange={(e) => setField("decor_id", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="">Decor</option>
                {decors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code ? `${d.code} - ` : ""}
                    {d.name}
                  </option>
                ))}
              </select>

              <input
                placeholder="Color"
                value={form.color || ""}
                onChange={(e) => setField("color", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Finish"
                value={form.finish || ""}
                onChange={(e) => setField("finish", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Thickness"
                value={form.thickness || ""}
                onChange={(e) => setField("thickness", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Width"
                value={form.width || ""}
                onChange={(e) => setField("width", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Length"
                value={form.length || ""}
                onChange={(e) => setField("length", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
              <input
                placeholder="Weight"
                value={form.weight || ""}
                onChange={(e) => setField("weight", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-4">Inventory and pricing</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={form.tracking_mode || "QUANTITY"}
                onChange={(e) => setField("tracking_mode", e.target.value)}
                className="border rounded-lg px-3 py-2"
              >
                <option value="NONE">NONE</option>
                <option value="QUANTITY">QUANTITY</option>
                <option value="SERIAL">SERIAL</option>
                <option value="BATCH">BATCH</option>
              </select>

              <input
                placeholder="Default purchase price"
                value={form.default_purchase_price || ""}
                onChange={(e) => setField("default_purchase_price", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <input
                placeholder="Default issue price"
                value={form.default_issue_price || ""}
                onChange={(e) => setField("default_issue_price", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <input
                placeholder="Currency"
                value={form.currency || "RSD"}
                onChange={(e) => setField("currency", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <input
                placeholder="Min stock qty"
                value={form.min_stock_qty || ""}
                onChange={(e) => setField("min_stock_qty", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <input
                placeholder="Reorder qty"
                value={form.reorder_qty || ""}
                onChange={(e) => setField("reorder_qty", e.target.value)}
                className="border rounded-lg px-3 py-2"
              />

              <label className="flex items-center gap-2 border rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={Number(form.is_active || 0) === 1}
                  onChange={(e) => setField("is_active", e.target.checked ? 1 : 0)}
                />
                Active
              </label>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Attributes</h2>
              <button
                type="button"
                onClick={addAttributeRow}
                className="px-3 py-2 rounded-lg border"
              >
                Add Attribute
              </button>
            </div>

            {(form.attributes || []).length === 0 ? (
              <div className="text-slate-500 text-sm">No attributes yet.</div>
            ) : (
              <div className="space-y-3">
                {form.attributes.map((attr, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <select
                      value={attr.attribute_id || ""}
                      onChange={(e) => onSelectAttribute(index, e.target.value)}
                      className="border rounded-lg px-3 py-2"
                    >
                      <option value="">Attribute</option>
                      {attributeCatalog.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} - {a.name}
                        </option>
                      ))}
                    </select>

                    <input
                      placeholder="Attribute name"
                      value={attr.attribute_name || ""}
                      readOnly
                      className="border rounded-lg px-3 py-2 bg-slate-50"
                    />

                    {attr.value_type === "number" ? (
                      <input
                        placeholder="Numeric value"
                        value={attr.value_number ?? ""}
                        onChange={(e) =>
                          setAttributeField(index, "value_number", e.target.value)
                        }
                        className="border rounded-lg px-3 py-2 md:col-span-2"
                      />
                    ) : attr.value_type === "boolean" ? (
                      <select
                        value={attr.value_boolean}
                        onChange={(e) =>
                          setAttributeField(index, "value_boolean", e.target.value)
                        }
                        className="border rounded-lg px-3 py-2 md:col-span-2"
                      >
                        <option value="">Select</option>
                        <option value={1}>Yes</option>
                        <option value={0}>No</option>
                      </select>
                    ) : attr.value_type === "json" ? (
                      <textarea
                        placeholder='{"key":"value"}'
                        value={attr.value_json || ""}
                        onChange={(e) =>
                          setAttributeField(index, "value_json", e.target.value)
                        }
                        className="border rounded-lg px-3 py-2 md:col-span-2 min-h-[90px] font-mono text-sm"
                      />
                    ) : (
                      <input
                        placeholder="Value"
                        value={attr.value_text || ""}
                        onChange={(e) =>
                          setAttributeField(index, "value_text", e.target.value)
                        }
                        className="border rounded-lg px-3 py-2 md:col-span-2"
                      />
                    )}

                    <button
                      type="button"
                      onClick={() => removeAttributeRow(index)}
                      className="px-3 py-2 rounded-lg border text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Images</h2>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  disabled={!editingId || uploadingImage}
                  onChange={(e) => handleUploadImage(e.target.files?.[0])}
                  className="border rounded-lg px-3 py-2"
                />
                {!editingId ? (
                  <span className="text-sm text-slate-500">
                    Save item first
                  </span>
                ) : null}
              </div>
            </div>

            {uploadingImage ? (
              <div className="text-sm text-slate-500">Uploading image...</div>
            ) : null}

            {(form.images || []).length === 0 ? (
              <div className="text-slate-500 text-sm">
                No uploaded images yet.
              </div>
            ) : (
              <div className="space-y-3">
                {form.images.map((img) => (
                  <div
                    key={img.id}
                    className="flex flex-col md:flex-row md:items-center gap-4 border rounded-lg p-3"
                  >
                    <img
                      src={normalizeImageUrl(img.image_url)}
                      alt="item"
                      className="h-24 w-24 object-cover rounded border"
                    />

                    <div className="flex-1 text-sm space-y-1">
                      <div>
                        <span className="font-medium">Type:</span>{" "}
                        {img.image_type || "-"}
                      </div>
                      <div>
                        <span className="font-medium">Primary:</span>{" "}
                        {Number(img.is_primary) === 1 ? "Yes" : "No"}
                      </div>
                      <div>
                        <span className="font-medium">Sort:</span>{" "}
                        {img.sort_order || 0}
                      </div>
                      <div className="text-slate-500">
                        {img.file_name || img.image_url}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {Number(img.is_primary) !== 1 ? (
                        <button
                          type="button"
                          onClick={() => handleSetPrimaryImage(img)}
                          className="px-3 py-2 rounded-lg border"
                        >
                          Set Primary
                        </button>
                      ) : null}

                      <button
                        type="button"
                        onClick={() => handleDeleteImage(img.id)}
                        className="px-3 py-2 rounded-lg border text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-4">Advanced spec JSON</h2>
            <textarea
              placeholder='{"material":"MDF","density":"720"}'
              value={form.spec_json || ""}
              onChange={(e) => setField("spec_json", e.target.value)}
              className="w-full border rounded-lg px-3 py-2 min-h-[180px] font-mono text-sm"
            />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-4">Search Items</h2>
            <input
              placeholder="Search by SKU, name, brand..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 mb-3"
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active only
            </label>
          </div>

          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b font-semibold">
              Items ({filteredRows.length})
            </div>

            <div className="max-h-[900px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">SKU</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-right">Min</th>
                    <th className="p-2 text-right">Reorder</th>
                    <th className="p-2 text-center">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-slate-500">
                        Loading...
                      </td>
                    </tr>
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="p-4 text-center text-slate-500">
                        No resource items found.
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r) => (
                      <tr
                        key={r.id}
                        className={`border-t ${
                          Number(editingId) === Number(r.id) ? "bg-blue-50" : ""
                        }`}
                      >
                        <td className="p-2">{r.sku}</td>
                        <td className="p-2">{r.name}</td>
                        <td className="p-2 text-right">
                          {Number(r.min_stock_qty || 0).toFixed(4)}
                        </td>
                        <td className="p-2 text-right">
                          {Number(r.reorder_qty || 0).toFixed(4)}
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => startEdit(r)}
                            className="text-blue-600"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-3">Current selection</h2>
            {selectedItemPreview ? (
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">SKU:</span> {selectedItemPreview.sku}
                </div>
                <div>
                  <span className="font-medium">Name:</span> {selectedItemPreview.name}
                </div>
                <div>
                  <span className="font-medium">Brand:</span>{" "}
                  {selectedItemPreview.brand || "-"}
                </div>
                <div>
                  <span className="font-medium">Model:</span>{" "}
                  {selectedItemPreview.model || "-"}
                </div>
                <div>
                  <span className="font-medium">Kind:</span>{" "}
                  {selectedItemPreview.item_kind || "-"}
                </div>
                <div>
                  <span className="font-medium">Tracking:</span>{" "}
                  {selectedItemPreview.tracking_mode || "-"}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">No item selected.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}